import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
	TShopResponsePayload,
	TShopGoodsRequest,
	TShopGoodsResponsePayload,
	TShopStore,
	TShopRequestPayload,
	TShopDeleteRequestPayload,
	TShopAttributesFormRequestPayload,
	TShopAttributesFormPayload,
	TShopAddResponsePayload,
	TShopMapFieldsPayload,
	TShopAttributesRequestPayload,
	TShopAttributesResponsePayload,
	TShopMigrateRequestPayload,
	TShopMigrateResponsePayload,
	TShopMigrationStatusRequestPayload,
	TShopRestartMigrationRequestPayload,
	TShopRestartMigrationResponsePayload,
} from './types';

export const initialState: TShopStore = {
	shops: null,
	shopsLoading: false,
	shopGoods: null,
	shopGoodsLoading: false,
	shopAddLoading: false,
	shopAddError: null,
	shopDeleteLoading: false,
	shopAttributesForm: null,
	shopAttributesFormLoading: false,
	newShopId: null,
	shopAttributes: null,
	shopAttributesLoading: false,
	shopMigrate: null,
	shopMigrateLoading: false,
	shopMigrationStatus: {},
	shopMigrationStatusLoading: false,
	shopRestartMigration: null,
	shopRestartMigrationLoading: false,
};

const shops = createSlice({
	name: '@@shops',
	initialState,
	reducers: {
		getShopsRequest: (state: TShopStore) => {
			const shopsState = state;
			shopsState.shopsLoading = true;
		},
		getShopsSuccess: (state: TShopStore, action: PayloadAction<TShopResponsePayload>) => {
			const shopsState = state;
			shopsState.shops = action.payload;
			shopsState.shopsLoading = false;
		},
		getShopGoodsRequest: (state: TShopStore, action: PayloadAction<TShopGoodsRequest>) => {
			const shopGoodsState = state;
			shopGoodsState.shopGoodsLoading = true;
		},
		getShopGoodsSuccess: (state: TShopStore, action: PayloadAction<TShopGoodsResponsePayload>) => {
			const shopGoodsState = state;
			shopGoodsState.shopGoods = action.payload;
			shopGoodsState.shopGoodsLoading = false;
		},
		getShopGoodsFailure: (state: TShopStore) => {
			const shopGoodsState = state;
			shopGoodsState.shopGoodsLoading = false;
		},
		createShopGoodsRequest: (state: TShopStore) => {
			const shopGoodsState = state;
			shopGoodsState.shopGoodsLoading = true;
		},
		createShopGoodsSuccess: (
			state: TShopStore,
			action: PayloadAction<TShopGoodsResponsePayload>,
		) => {
			const shopGoodsState = state;
			shopGoodsState.shopGoods = action.payload;
			shopGoodsState.shopGoodsLoading = false;
		},
		createShopGoodsFailure: (state: TShopStore) => {
			const shopGoodsState = state;
			shopGoodsState.shopGoodsLoading = false;
		},
		addShopRequest: (state: TShopStore, action: PayloadAction<TShopRequestPayload>) => {
			const shopAddState = state;
			shopAddState.shopAddLoading = true;
			shopAddState.shopAddError = null;
		},
		addShopSuccess: (state: TShopStore, action: PayloadAction<TShopAddResponsePayload>) => {
			const shopAddState = state;
			shopAddState.shopAddLoading = false;
			shopAddState.newShopId = action.payload;
			shopAddState.shopAddError = null;
		},
		addShopFailure: (state: TShopStore, action: PayloadAction<string | null>) => {
			const shopAddState = state;
			shopAddState.shopAddLoading = false;
			shopAddState.shopAddError = action.payload;
		},
		deleteShopRequest: (state: TShopStore, action: PayloadAction<TShopDeleteRequestPayload>) => {
			const shopDeleteState = state;
			shopDeleteState.shopDeleteLoading = true;
		},
		deleteShopSuccess: (state: TShopStore) => {
			const shopDeleteState = state;
			shopDeleteState.shopDeleteLoading = false;
		},
		getShopAttributesFormRequest: (
			state: TShopStore,
			action: PayloadAction<TShopAttributesFormRequestPayload>,
		) => {
			const shopAttributesFormState = state;
			shopAttributesFormState.shopAttributesFormLoading = true;
		},
		getShopAttributesFormSuccess: (
			state: TShopStore,
			action: PayloadAction<TShopAttributesFormPayload>,
		) => {
			const shopAttributesFormState = state;
			shopAttributesFormState.shopAttributesForm = action.payload;
			shopAttributesFormState.shopAttributesFormLoading = false;
		},
		getShopAttributesFormFailure: (state: TShopStore) => {
			const shopAttributesFormState = state;
			shopAttributesFormState.shopAttributesFormLoading = false;
		},
		mapShopFieldsRequest: (state: TShopStore, action: PayloadAction<TShopMapFieldsPayload>) => {
			const shopMapFieldsState = state;
			shopMapFieldsState.shopAddLoading = true;
		},
		mapShopFieldsSuccess: (state: TShopStore) => {
			const shopMapFieldsState = state;
			shopMapFieldsState.shopAddLoading = false;
		},
		updateShopAttributesRequest: (
			state: TShopStore,
			action: PayloadAction<TShopAttributesRequestPayload>,
		) => {
			const shopAttributesState = state;
			shopAttributesState.shopAttributesLoading = true;
		},
		updateShopAttributesSuccess: (
			state: TShopStore,
			action: PayloadAction<TShopAttributesResponsePayload>,
		) => {
			const shopAttributesState = state;
			shopAttributesState.shopAttributes = action.payload;
			shopAttributesState.shopAttributesLoading = false;
		},
		updateShopAttributesFailure: (state: TShopStore) => {
			const shopAttributesState = state;
			shopAttributesState.shopAttributesLoading = false;
		},
		migrateShopRequest: (state: TShopStore, action: PayloadAction<TShopMigrateRequestPayload>) => {
			const shopMigrateState = state;
			shopMigrateState.shopMigrateLoading = true;
		},
		migrateShopSuccess: (state: TShopStore, action: PayloadAction<TShopMigrateResponsePayload>) => {
			const shopMigrateState = state;
			shopMigrateState.shopMigrate = action.payload;
			shopMigrateState.shopMigrateLoading = false;
		},
		migrateShopFailure: (state: TShopStore) => {
			const shopMigrateState = state;
			shopMigrateState.shopMigrateLoading = false;
		},
		fetchShopMigrationStatusRequest: (
			state: TShopStore,
			action: PayloadAction<TShopMigrationStatusRequestPayload>,
		) => {
			const shopMigrationStatusState = state;
			shopMigrationStatusState.shopMigrationStatusLoading = true;
		},
		fetchShopMigrationStatusSuccess: (
			state: TShopStore,
			action: PayloadAction<{ shopId: number; status: number }>,
		) => {
			const shopMigrationStatusState = state;
			shopMigrationStatusState.shopMigrationStatus = {
				...shopMigrationStatusState.shopMigrationStatus,
				[action.payload.shopId]: action.payload.status,
			};
			shopMigrationStatusState.shopMigrationStatusLoading = false;
		},
		fetchShopMigrationStatusFailure: (state: TShopStore) => {
			const shopMigrationStatusState = state;
			shopMigrationStatusState.shopMigrationStatusLoading = false;
		},
		restartShopMigrationRequest: (
			state: TShopStore,
			action: PayloadAction<TShopRestartMigrationRequestPayload>,
		) => {
			const shopRestartMigrationState = state;
			shopRestartMigrationState.shopRestartMigrationLoading = true;
		},
		restartShopMigrationSuccess: (
			state: TShopStore,
			action: PayloadAction<TShopRestartMigrationResponsePayload>,
		) => {
			const shopRestartMigrationState = state;
			shopRestartMigrationState.shopRestartMigration = action.payload;
			shopRestartMigrationState.shopRestartMigrationLoading = false;
		},
		restartShopMigrationFailure: (state: TShopStore) => {
			const shopRestartMigrationState = state;
			shopRestartMigrationState.shopRestartMigrationLoading = false;
		},
		shopsInitState: () => initialState,
	},
});

export const {
	getShopsRequest,
	getShopsSuccess,
	getShopGoodsRequest,
	getShopGoodsSuccess,
	createShopGoodsRequest,
	createShopGoodsSuccess,
	createShopGoodsFailure,
	addShopRequest,
	addShopSuccess,
	addShopFailure,
	deleteShopRequest,
	deleteShopSuccess,
	getShopAttributesFormRequest,
	getShopAttributesFormSuccess,
	getShopAttributesFormFailure,
	mapShopFieldsRequest,
	mapShopFieldsSuccess,
	updateShopAttributesRequest,
	updateShopAttributesSuccess,
	updateShopAttributesFailure,
	migrateShopRequest,
	migrateShopSuccess,
	migrateShopFailure,
	fetchShopMigrationStatusRequest,
	fetchShopMigrationStatusSuccess,
	fetchShopMigrationStatusFailure,
	restartShopMigrationRequest,
	restartShopMigrationSuccess,
	restartShopMigrationFailure,
	shopsInitState,
} = shops.actions;

export default shops.reducer;
